// Queue Summary / Report view — aggregates a set of tickets into an at-a-glance
// ITSM control-room dashboard: volume KPIs, service-performance metrics (SLA
// attainment, MTTR, first-response, CSAT), status/priority/type breakdowns,
// agent workload, backlog aging and a created-vs-resolved trend.
// Shared by the unified Service Desk queue and the per-portal queue. Pure
// client-side; no extra API calls.
import React, { useMemo } from 'react';
import {
  Ticket, CheckCircle, WarningCircle, Clock, Gauge, Timer,
  ChatCircleDots, Smiley, Stack, TrendUp,
} from '@phosphor-icons/react';
import {
  STATUS_OPTIONS, PRIORITY_OPTIONS, CATEGORY_OPTIONS,
  statusLabel, priorityLabel, categoryLabel,
  STATUS_DOT, PRIORITY_DOT,
} from './ticketMeta';

const OPEN_STATUSES = ['open', 'in_progress', 'pending'];
const DONE_STATUSES = ['resolved', 'closed'];

const CATEGORY_BAR = {
  service_request: 'bg-blue-500',
  incident: 'bg-red-500',
  problem: 'bg-violet-500',
  change: 'bg-amber-500',
};

// ── helpers ──────────────────────────────────────────────────────────────
function countBy(list, keyFn) {
  const m = {};
  for (const t of list) { const k = keyFn(t); m[k] = (m[k] || 0) + 1; }
  return m;
}
const diffMins = (a, b) => (new Date(a).getTime() - new Date(b).getTime()) / 60000;
function median(arr) {
  if (!arr.length) return null;
  const s = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}
function fmtDur(mins) {
  if (mins == null || !isFinite(mins)) return '—';
  const m = Math.max(0, Math.round(mins));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}
function slaBucket(t) {
  const res = t.sla_state?.resolution;
  if (!res) return 'none';
  if (res.completed) return res.met ? 'met' : 'missed';
  if (res.breached) return 'breached';
  if ((res.remaining_mins ?? 1e9) <= 60) return 'at_risk';
  return 'on_track';
}
const AV_PALETTE = ['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-violet-500', 'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-orange-500'];
function avatarColor(str) { let h = 0; for (const c of String(str || '')) h = (h * 31 + c.charCodeAt(0)) >>> 0; return AV_PALETTE[h % AV_PALETTE.length]; }
function initials(name) {
  const p = String(name || '?').trim().split(/\s+/).filter(Boolean);
  if (!p.length) return '?';
  return (p.length === 1 ? p[0][0] : p[0][0] + p[p.length - 1][0]).toUpperCase();
}
const dayKey = (d) => new Date(d).toISOString().slice(0, 10);

// ── KPI cell (control-room strip) ─────────────────────────────────────────
// Enterprise treatment: neutral dark figures, a single semantic left-accent
// bar, restrained sublines. Colour signals health, never decoration.
function Kpi({ icon: Icon, label, value, sub, subTone = 'text-zinc-400', bar = 'bg-slate-300', testid }) {
  return (
    <div className="relative flex-1 min-w-[150px] px-5 py-4" data-testid={testid}>
      <span className={`absolute left-0 top-4 bottom-4 w-[3px] rounded-full ${bar}`} />
      <div className="flex items-center gap-1.5 mb-2 pl-1">
        <Icon size={13} weight="fill" className="text-zinc-400" />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">{label}</span>
      </div>
      <p className="text-[34px] leading-none font-black font-['Outfit'] tabular-nums pl-1 text-zinc-900">{value}</p>
      {sub && <p className={`text-[11px] mt-1.5 pl-1 font-medium ${subTone}`}>{sub}</p>}
    </div>
  );
}

function Panel({ title, icon: Icon, meta, children, testid, className = '' }) {
  return (
    <div className={`bg-white border border-zinc-200 rounded-xl overflow-hidden ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-zinc-100">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={14} weight="fill" className="text-zinc-400" />}
          <h3 className="text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-500">{title}</h3>
        </div>
        {meta != null && <span className="text-[11px] text-zinc-400 font-medium tabular-nums">{meta}</span>}
      </div>
      <div className="px-4 sm:px-5 py-4 space-y-3">{children}</div>
    </div>
  );
}

function BreakdownBar({ label, count, total, dot, bar }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3" data-testid={`summary-row-${label}`}>
      <div className="flex items-center gap-2 w-20 sm:w-28 shrink-0">
        {dot && <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />}
        <span className="text-[13px] text-zinc-600 truncate">{label}</span>
      </div>
      <div className="flex-1 min-w-0 h-2.5 rounded-full bg-zinc-100 overflow-hidden">
        <div className={`h-full rounded-full ${bar} transition-[width] duration-700 ease-out`} style={{ width: `${pct}%` }} />
      </div>
      <div className="w-12 sm:w-16 text-right shrink-0">
        <span className="text-sm font-black text-zinc-900 tabular-nums font-['Outfit']">{count}</span>
        <span className="text-[11px] text-zinc-400 ml-1 tabular-nums">{pct}%</span>
      </div>
    </div>
  );
}

export default function QueueSummary({ tickets = [], memberById = {} }) {
  const s = useMemo(() => {
    const total = tickets.length;
    const open = tickets.filter(t => OPEN_STATUSES.includes(t.status)).length;
    const done = tickets.filter(t => DONE_STATUSES.includes(t.status)).length;
    const byStatus = countBy(tickets, t => t.status || 'open');
    const byPriority = countBy(tickets, t => t.priority || 'medium');
    const byCategory = countBy(tickets, t => t.category || 'service_request');
    const bySla = countBy(tickets, slaBucket);

    const breached = (bySla.breached || 0) + (bySla.missed || 0);
    const atRisk = bySla.at_risk || 0;
    const met = bySla.met || 0;
    const slaDenom = met + breached;
    const slaAttainment = slaDenom ? Math.round((met / slaDenom) * 100) : null;
    const resolutionRate = total ? Math.round((done / total) * 100) : 0;

    // MTTR (median resolution) + avg first response.
    const resolveMins = tickets.filter(t => t.resolved_at && t.created_at).map(t => diffMins(t.resolved_at, t.created_at)).filter(v => v >= 0);
    const mttr = median(resolveMins);
    const frMins = tickets.filter(t => t.first_responded_at && t.created_at).map(t => diffMins(t.first_responded_at, t.created_at)).filter(v => v >= 0);
    const avgFr = frMins.length ? frMins.reduce((a, b) => a + b, 0) / frMins.length : null;

    // CSAT (Good share, Jira/Zendesk convention).
    const rated = tickets.filter(t => t.csat?.rating);
    const good = rated.filter(t => t.csat.rating === 'good').length;
    const csatPct = rated.length ? Math.round((good / rated.length) * 100) : null;

    // Agent workload — open tickets per assignee, busiest first.
    const workloadMap = {};
    for (const t of tickets) {
      if (!OPEN_STATUSES.includes(t.status)) continue;
      const id = t.assignee_user_id || '__unassigned__';
      workloadMap[id] = (workloadMap[id] || 0) + 1;
    }
    const workload = Object.entries(workloadMap)
      .map(([id, count]) => ({ id, name: id === '__unassigned__' ? 'Unassigned' : (memberById[id]?.name || 'Unknown agent'), count, unassigned: id === '__unassigned__' }))
      .sort((a, b) => b.count - a.count).slice(0, 6);

    // Backlog aging — unresolved tickets by age.
    const now = Date.now();
    const aging = { d1: 0, d3: 0, d7: 0, d7p: 0 };
    for (const t of tickets) {
      if (DONE_STATUSES.includes(t.status)) continue;
      const ageH = (now - new Date(t.created_at).getTime()) / 3600000;
      if (ageH <= 24) aging.d1++; else if (ageH <= 72) aging.d3++; else if (ageH <= 168) aging.d7++; else aging.d7p++;
    }
    const backlog = aging.d1 + aging.d3 + aging.d7 + aging.d7p;

    // Created vs Resolved — last 14 days.
    const days = [];
    for (let i = 13; i >= 0; i--) { const d = new Date(now - i * 86400000); days.push({ key: dayKey(d), date: d }); }
    const createdByDay = countBy(tickets.filter(t => t.created_at), t => dayKey(t.created_at));
    const resolvedByDay = countBy(tickets.filter(t => t.resolved_at), t => dayKey(t.resolved_at));
    const trend = days.map(d => ({ ...d, created: createdByDay[d.key] || 0, resolved: resolvedByDay[d.key] || 0 }));
    const trendMax = Math.max(1, ...trend.map(d => Math.max(d.created, d.resolved)));

    return { total, open, done, byStatus, byPriority, byCategory, breached, atRisk, met, slaAttainment, resolutionRate, mttr, avgFr, csatPct, csatCount: rated.length, workload, aging, backlog, trend, trendMax };
  }, [tickets, memberById]);

  const maxWorkload = Math.max(1, ...s.workload.map(w => w.count));
  const openPct = s.total ? Math.round((s.open / s.total) * 100) : 0;

  const agingRows = [
    { label: '< 24 hours', count: s.aging.d1, bar: 'bg-emerald-500', dot: 'bg-emerald-500' },
    { label: '1 – 3 days', count: s.aging.d3, bar: 'bg-amber-500', dot: 'bg-amber-500' },
    { label: '3 – 7 days', count: s.aging.d7, bar: 'bg-orange-500', dot: 'bg-orange-500' },
    { label: '> 7 days', count: s.aging.d7p, bar: 'bg-red-500', dot: 'bg-red-500' },
  ];

  return (
    <div className="space-y-4" data-testid="sd-queue-summary">
      {/* ── Volume KPIs — control-room strip ── */}
      <div className="bg-white border border-zinc-200 rounded-xl flex flex-wrap divide-x divide-zinc-100">
        <Kpi icon={Ticket} label="Total" value={s.total} sub="in scope" bar="bg-slate-800" testid="summary-stat-total" />
        <Kpi icon={Clock} label="Open / active" value={s.open} sub={`${openPct}% of total`} bar="bg-indigo-500" testid="summary-stat-open" />
        <Kpi icon={WarningCircle} label="Overdue" value={s.breached} sub="SLA breached" subTone={s.breached ? 'text-rose-600' : 'text-zinc-400'} bar={s.breached ? 'bg-rose-500' : 'bg-slate-300'} testid="summary-stat-breached" />
        <Kpi icon={Timer} label="At risk" value={s.atRisk} sub="< 1h to breach" subTone={s.atRisk ? 'text-amber-600' : 'text-zinc-400'} bar={s.atRisk ? 'bg-amber-500' : 'bg-slate-300'} testid="summary-stat-atrisk" />
      </div>

      {/* ── Service performance ── */}
      <div className="bg-white border border-zinc-200 rounded-xl flex flex-wrap divide-x divide-zinc-100">
        <Kpi icon={Gauge} label="SLA attainment" value={s.slaAttainment == null ? '—' : `${s.slaAttainment}%`}
          sub={s.slaAttainment == null ? 'no closed SLAs yet' : `${s.met} met · ${s.breached} missed`}
          subTone={s.slaAttainment == null ? 'text-zinc-400' : s.slaAttainment >= 90 ? 'text-emerald-600' : s.slaAttainment >= 70 ? 'text-amber-600' : 'text-rose-600'}
          bar={s.slaAttainment == null ? 'bg-slate-300' : s.slaAttainment >= 90 ? 'bg-emerald-500' : s.slaAttainment >= 70 ? 'bg-amber-500' : 'bg-rose-500'}
          testid="summary-stat-slapct" />
        <Kpi icon={CheckCircle} label="Resolution rate" value={`${s.resolutionRate}%`} sub={`${s.done} resolved / closed`} bar="bg-indigo-500" testid="summary-stat-resrate" />
        <Kpi icon={Timer} label="MTTR" value={fmtDur(s.mttr)} sub="median resolution" bar="bg-slate-300" testid="summary-stat-mttr" />
        <Kpi icon={ChatCircleDots} label="First response" value={fmtDur(s.avgFr)} sub="average time" bar="bg-slate-300" testid="summary-stat-fr" />
        <Kpi icon={Smiley} label="CSAT" value={s.csatPct == null ? '—' : `${s.csatPct}%`}
          sub={s.csatCount ? `${s.csatCount} rated` : 'no ratings yet'}
          subTone={s.csatPct == null ? 'text-zinc-400' : s.csatPct >= 80 ? 'text-emerald-600' : s.csatPct >= 50 ? 'text-amber-600' : 'text-rose-600'}
          bar={s.csatPct == null ? 'bg-slate-300' : s.csatPct >= 80 ? 'bg-emerald-500' : s.csatPct >= 50 ? 'bg-amber-500' : 'bg-rose-500'}
          testid="summary-stat-csat" />
      </div>

      {/* ── Breakdowns ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="By status" icon={Stack} meta={`${s.total} tickets`} testid="summary-by-status">
          {STATUS_OPTIONS.filter(x => s.byStatus[x]).map(x => (
            <BreakdownBar key={x} label={statusLabel(x)} count={s.byStatus[x] || 0} total={s.total} dot={STATUS_DOT[x]} bar={STATUS_DOT[x]} />
          ))}
          {s.total === 0 && <p className="text-sm text-zinc-400">No tickets.</p>}
        </Panel>

        <Panel title="By priority" icon={Gauge} testid="summary-by-priority">
          {PRIORITY_OPTIONS.filter(x => s.byPriority[x]).map(x => (
            <BreakdownBar key={x} label={priorityLabel(x)} count={s.byPriority[x] || 0} total={s.total} dot={PRIORITY_DOT[x]} bar={PRIORITY_DOT[x]} />
          ))}
          {s.total === 0 && <p className="text-sm text-zinc-400">No tickets.</p>}
        </Panel>

        <Panel title="By type" icon={Ticket} testid="summary-by-type">
          {CATEGORY_OPTIONS.filter(x => s.byCategory[x]).map(x => (
            <BreakdownBar key={x} label={categoryLabel(x)} count={s.byCategory[x] || 0} total={s.total} dot={CATEGORY_BAR[x]} bar={CATEGORY_BAR[x] || 'bg-zinc-400'} />
          ))}
          {s.total === 0 && <p className="text-sm text-zinc-400">No tickets.</p>}
        </Panel>

        <Panel title="Agent workload" icon={Clock} meta="open" testid="summary-workload">
          {s.workload.length === 0 && <p className="text-sm text-zinc-400">No open tickets.</p>}
          {s.workload.map(w => {
            const pct = Math.round((w.count / maxWorkload) * 100);
            return (
              <div key={w.id} className="flex items-center gap-3" data-testid={`summary-workload-${w.id}`}>
                <div className="flex items-center gap-2 w-28 sm:w-40 shrink-0">
                  {w.unassigned ? (
                    <span className="w-6 h-6 rounded-full bg-zinc-100 ring-1 ring-zinc-200 flex items-center justify-center shrink-0"><Clock size={12} className="text-zinc-400" /></span>
                  ) : (
                    <span className={`w-6 h-6 rounded-full ${avatarColor(w.name)} text-white text-[10px] font-bold flex items-center justify-center shrink-0`}>{initials(w.name)}</span>
                  )}
                  <span className={`text-[13px] truncate ${w.unassigned ? 'text-zinc-400 italic' : 'text-zinc-700'}`}>{w.name}</span>
                </div>
                <div className="flex-1 min-w-0 h-2.5 rounded-full bg-zinc-100 overflow-hidden">
                  <div className={`h-full rounded-full ${w.unassigned ? 'bg-zinc-300' : 'bg-indigo-500'} transition-[width] duration-700 ease-out`} style={{ width: `${pct}%` }} />
                </div>
                <span className="text-sm font-black text-zinc-900 w-8 text-right tabular-nums font-['Outfit']">{w.count}</span>
              </div>
            );
          })}
        </Panel>
      </div>

      {/* ── Backlog aging + trend ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Backlog aging" icon={Timer} meta={`${s.backlog} unresolved`} testid="summary-backlog-aging">
          {s.backlog === 0 && <p className="text-sm text-zinc-400">Nothing in the backlog. Clean queue. 🎉</p>}
          {s.backlog > 0 && agingRows.map(r => (
            <BreakdownBar key={r.label} label={r.label} count={r.count} total={s.backlog} dot={r.dot} bar={r.bar} />
          ))}
        </Panel>

        <Panel title="Created vs Resolved" icon={TrendUp} meta="last 14 days" testid="summary-trend">
          <div className="flex items-center gap-4 mb-1">
            <span className="inline-flex items-center gap-1.5 text-[11px] text-zinc-500"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500" /> Created</span>
            <span className="inline-flex items-center gap-1.5 text-[11px] text-zinc-500"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Resolved</span>
          </div>
          <div className="flex items-end justify-between gap-[3px] h-28 pt-2">
            {s.trend.map((d, i) => (
              <div key={d.key} className="flex-1 h-full flex flex-col items-center justify-end group relative" data-testid={`summary-trend-day-${i}`}>
                <div className="absolute -top-1 opacity-0 group-hover:opacity-100 transition-opacity text-[10px] font-semibold text-zinc-600 bg-white px-1 rounded shadow-sm ring-1 ring-zinc-200 whitespace-nowrap z-10 pointer-events-none">
                  +{d.created} / −{d.resolved}
                </div>
                <div className="w-full flex items-end justify-center gap-[2px] h-full">
                  <div className="w-1/2 max-w-[7px] rounded-t-sm bg-blue-500/85" style={{ height: `${Math.max(d.created ? 4 : 0, (d.created / s.trendMax) * 100)}%` }} />
                  <div className="w-1/2 max-w-[7px] rounded-t-sm bg-emerald-500/85" style={{ height: `${Math.max(d.resolved ? 4 : 0, (d.resolved / s.trendMax) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-zinc-400 font-medium pt-2 border-t border-zinc-100 mt-1">
            <span>{s.trend[0]?.date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
            <span>{s.trend[Math.floor(s.trend.length / 2)]?.date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
            <span>Today</span>
          </div>
        </Panel>
      </div>
    </div>
  );
}
